import { AxiosRequestConfig } from 'axios';

import { nextTurnstileToken, turnstileHeaders } from '@components/Turnstile/Turnstile';
import {
  Clinician,
  IClinic,
  ICommunicationPreference,
  IExtendedClinician,
  OrganizationRole,
} from '@customTypes/clinician';
import { Guidance } from '@customTypes/drugs';
import { OrderResult } from '@customTypes/order';
import { IPatientDetailRes, PatientRequisitionDetails } from '@customTypes/patient';
import { Requisition, RerequisitionType } from '@customTypes/requisition';
import { ICreateProviderForm, ISignupForm } from '@customTypes/user';
import { setOrganizationRoleFromClinic } from '@helpers/clinician';
import axios from '@hooks/useAxios';

export * from './draftOrderActions';
export * from './requisitionActions';
export { getUserProfile } from './userProfile';

export const getOrderResultsById = async (orderId: string): Promise<OrderResult[]> => {
  return axios.get(`/order/${orderId}`).then(res => res.data.results);
};

export const fetchAllClinicians = async (): Promise<Clinician[]> => {
  return axios.get('/clinician').then(res => res.data.results);
};

export const fetchProductsList = async () => {
  return axios.get('/product').then(res => res.data);
};

// Example for YYYY-MM-DD format
const yyyyMmDdRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

function isValidDateFormat(dateString: string, formatRegex = yyyyMmDdRegex) {
  return formatRegex.test(dateString);
}

export interface RequisitionSubjectFilters {
  dob?: string;
  sex?: string;
  addressRegion?: string;
  addressCountry?: string;
}

// Flip to true to mount the requisition widget and attach CF-Turnstile-Response again.
export const ATTACH_REQUISITION_TURNSTILE = false;

export const fetchBaseRequisitionJSONSchema = async (
  productIds: string[],
  relative: { requisition?: string; dob?: string } | undefined,
  config: AxiosRequestConfig = {},
  campaignCode?: string,
  rerequisitionId?: string,
  rerequisitionType?: RerequisitionType,
  subject?: RequisitionSubjectFilters,
) => {
  const data: Record<string, unknown> = {
    selected_product_ids: productIds,
  };

  if (campaignCode) {
    data.campaign_code = campaignCode;
  }

  // Demographics narrow the orderable test list, so send whatever the patient step has so far.
  const subjectDemographics: Record<string, unknown> = {};
  if (subject?.dob && isValidDateFormat(subject.dob)) {
    subjectDemographics.dob = subject.dob;
  }
  if (subject?.sex) {
    subjectDemographics.sex = subject.sex;
  }
  if (subject?.addressRegion) {
    subjectDemographics.address_region = subject.addressRegion;
  }
  if (subject?.addressCountry) {
    subjectDemographics.address_country = subject.addressCountry;
  }
  if (Object.keys(subjectDemographics).length > 0) {
    data.subject_demographics = subjectDemographics;
  }

  if (rerequisitionId) {
    data.requisition_id = rerequisitionId;
  }

  if (rerequisitionType) {
    data.rerequisition_type = rerequisitionType;
  }

  if (relative?.requisition && relative.dob && relative.requisition.length > 5 && isValidDateFormat(relative.dob)) {
    data.relative_requisition = relative.requisition;
    data.relative_dob = relative.dob;
  }

  const turnstileToken = ATTACH_REQUISITION_TURNSTILE ? await nextTurnstileToken() : null;
  return axios
    .post('/provider/forms/get-requisition-form', data, {
      ...config,
      headers: {
        ...(config.headers as Record<string, string> | undefined),
        ...turnstileHeaders(turnstileToken),
      },
      skipResponseInterceptor: true,
    })
    .then(res => res.data);
};

export const submitRequisitionJSONSchemaForm = async (data: unknown, draftOrderId?: string): Promise<Requisition> => {
  const turnstileToken = ATTACH_REQUISITION_TURNSTILE ? await nextTurnstileToken() : null;
  return axios
    .post('/provider/forms/requisition', data, {
      params: {
        draft_id: draftOrderId,
      },
      headers: turnstileHeaders(turnstileToken),
      skipResponseInterceptor: true,
    })
    .then(res => res.data);
};

export const getMedicationGuidance = async (individual: string): Promise<Guidance> => {
  return axios.get(`/provider/individual/${individual}/medication-response/guidelines`).then(res => res.data);
};

export const getIndividualDetail = async (individual: string): Promise<PatientRequisitionDetails> => {
  return axios.get(`/provider/individual/${individual}`).then(res => res.data);
};

export const getRequisitionDetail = async (requisitionId: string): Promise<Requisition> => {
  return axios.get(`/requisition/${requisitionId}`).then(res => res.data);
};

export const getPatientDetail = async (patientId: string): Promise<IPatientDetailRes> => {
  return axios.get(`/patient/${patientId}`).then(res => res.data);
};

export const postOnboarding = async (data: ISignupForm): Promise<string> => {
  const turnstileToken = await nextTurnstileToken();
  // Remove id field from teammates because it's not acceptable in the payload
  return axios
    .post(
      '/provider/onboarding',
      {
        ...data,
        teammates: data.teammates.map(mate => {
          delete mate.id;

          return mate;
        }),
      },
      { headers: turnstileHeaders(turnstileToken) },
    )
    .then(res => res.data);
};

export const fetchOrganizationProviders = async (clinicId: string): Promise<Clinician[]> => {
  const perPage = 25;
  const allResults: Clinician[] = [];
  let page = 1;
  let hasNext = true;

  // `res.data.next` from LIMS is the upstream URL (not routable through our API
  // proxy), so we only use it as a "more results exist" signal and increment
  // page ourselves.
  while (hasNext) {
    // eslint-disable-next-line no-await-in-loop
    const res = await axios.get(`/provider/organization/${clinicId}/provider`, {
      params: { page, per_page: perPage },
    });
    allResults.push(...(res.data.results ?? []));
    hasNext = Boolean(res.data.next);
    page += 1;
  }

  return setOrganizationRoleFromClinic(allResults, clinicId);
};

export const fetchClinicalTeamByProvider = async (clinicId: string, providerId: string): Promise<Clinician[]> => {
  return axios
    .get(`/provider/organization/${clinicId}/provider/${providerId}/delegate`)
    .then(res => setOrganizationRoleFromClinic(res.data.results, clinicId));
};

export const addUserToClinicalTeam = async (
  clinicId: string,
  providerId: string,
  userId: string,
): Promise<Clinician[]> => {
  return axios
    .post(`/provider/organization/${clinicId}/provider/${providerId}/delegate`, { provider_id: userId })
    .then(res => res.data);
};

export const removeUserToClinicalTeam = async (
  clinicId: string,
  providerId: string,
  userId: string,
): Promise<string> => {
  return axios
    .delete(`/provider/organization/${clinicId}/provider/${providerId}/delegate/${userId}`)
    .then(res => res.data);
};

export const updateUserRoles = async (
  clinicId: string,
  providerId: string,
  roles: OrganizationRole[],
  communicationPreferences: ICommunicationPreference,
) => {
  return axios
    .patch(`/provider/organization/${clinicId}/provider/${providerId}`, {
      provider_roles: roles,
      communication_preferences: communicationPreferences,
    })
    .then(res => res.data);
};

export const getPatientRequisitions = async (patientId: string): Promise<Requisition[]> => {
  return axios.get(`/provider/individual/${patientId}/requisition`).then(res => res.data.results);
};

export const downgradeRequisition = async (
  mainRequisitionId: string,
  requisitionIdsToDowngrade: string[],
): Promise<string> => {
  return axios
    .post(`/provider/requisition/${mainRequisitionId}/downgrade`, {
      support_requisition_ids: requisitionIdsToDowngrade,
    })
    .then(res => res.data);
};

export const addNewProvider = async (orgId: string, payload: ICreateProviderForm): Promise<IExtendedClinician> => {
  return axios
    .post(`/provider/organization/${orgId}/provider`, payload, {
      skipResponseInterceptor: true,
    })
    .then(res => res.data);
};

export const downloadCollatedReport = async (requisitionId: string) => {
  return axios.get(`/provider/requisition/${requisitionId}/collated-report`, {
    responseType: 'blob',
  });
};

export const requestFaxVerification = async (clinicId: string): Promise<{ success: string }> => {
  return axios.post(`/provider/clinic/${clinicId}/fax/verify/request`).then(res => res.data);
};

export const confirmFaxVerification = async (clinicId: string, code: string): Promise<{ success: string }> => {
  return axios.post(`/provider/clinic/${clinicId}/fax/verify/confirm`, { code }).then(res => res.data);
};

export const getClinicDetails = async (clinicId: string): Promise<IClinic> => {
  return axios.get(`/provider/clinic/${clinicId}`).then(res => res.data);
};

export const updateClinicDetails = async (clinicId: string, payload: Partial<IClinic>): Promise<IClinic> => {
  return axios.patch(`/provider/clinic/${clinicId}`, payload).then(res => res.data);
};
